import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { CountryCode } from 'libphonenumber-js/min';
import { CountryPhone, DIAL_CODE_DEFAULT_COUNTRY } from './phone-input.models';
import { resolveDefaultCountry } from './default-country.utils';

export interface PhoneInputState {
  country: CountryPhone;
  nationalNumber: string;
}

export function findCountryByIso2(
  countries: CountryPhone[],
  iso2: string,
): CountryPhone | undefined {
  return countries.find((c) => c.iso2 === iso2);
}

export function cleanPhoneInput(value: string): string {
  let v = value.replace(/[^\d+]/g, '');
  const plusCount = (v.match(/\+/g) || []).length;
  if (plusCount > 1) v = v.replace(/\+/g, '');
  else if (v.indexOf('+') > 0) v = v.replace(/\+/g, '');
  if (v.startsWith('+') && v.length > 1) v = '+' + v.slice(1).replace(/\D/g, '');
  else if (!v.startsWith('+')) v = v.replace(/\D/g, '');
  return v;
}

export function parseE164(
  value: string | null,
  countries: CountryPhone[],
): PhoneInputState {
  if (!countries.length) {
    throw new Error('countries must not be empty');
  }
  if (value == null || value === '') {
    return { country: resolveDefaultCountry(countries), nationalNumber: '' };
  }
  const parsed = parsePhoneNumberFromString(value);
  if (parsed) {
    const country = parsed.country
      ? findCountryByIso2(countries, parsed.country)
      : undefined;
    return {
      country: country ?? resolveDefaultCountry(countries),
      nationalNumber: parsed.nationalNumber,
    };
  }
  return { country: resolveDefaultCountry(countries), nationalNumber: '' };
}

export function toE164(country: CountryPhone, nationalNumber: string): string | null {
  const raw = nationalNumber.trim();
  if (!raw) return null;
  if (raw.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(raw);
    return parsed?.isValid() ? parsed.format('E.164') : null;
  }
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const full = '+' + country.dialCode + digits;
  const parsed = parsePhoneNumberFromString(full, country.iso2 as CountryCode);
  return parsed?.isValid() ? parsed.format('E.164') : null;
}

export function tryParseFullNumber(
  cleanedInput: string,
  countries: CountryPhone[],
): PhoneInputState | null {
  if (!cleanedInput.startsWith('+') || !countries.length) {
    return null;
  }
  const parsed = parsePhoneNumberFromString(cleanedInput);
  if (!parsed?.country) {
    return null;
  }
  let iso2: string = parsed.country;
  if (!findCountryByIso2(countries, iso2)) {
    const callingCode = parsed.countryCallingCode;
    if (callingCode) {
      iso2 =
        DIAL_CODE_DEFAULT_COUNTRY[callingCode] ||
        countries.find((c) => c.dialCode === callingCode)?.iso2 ||
        iso2;
    }
  }
  const country = findCountryByIso2(countries, iso2);
  if (!country) {
    return null;
  }
  return { country, nationalNumber: parsed.nationalNumber };
}
